import { z } from "zod";

import { readLandingCookie } from "@/lib/analytics/landingCookie";

/**
 * Landing attribution carried from the sign-up form through the auth
 * provider's sign-up `unsafeMetadata` to the `user.created` hook, where it is
 * stitched to the new member. Same pattern as `lib/tolt/attribution.ts`.
 *
 * This is the path that survives an ad blocker or a closed tab: the
 * landing-cookie POST may never fire, but the sign-up call always carries this.
 * Shared client/server module — keep it free of server-only imports.
 */

export const LANDING_SIGNUP_METADATA_KEY = "landing";

export const SIGNUP_METHODS = ["google", "email"] as const;
export type SignupMethod = (typeof SIGNUP_METHODS)[number];

export const landingSignupMetadataSchema = z.object({
  visitorId: z.string().min(1).max(128).optional(),
  sourcePage: z.string().min(1).max(512).optional(),
  sourceCta: z.string().min(1).max(64).optional(),
  landingPath: z.string().min(1).max(2048).optional(),
  signupMethod: z.enum(SIGNUP_METHODS),
});

export type LandingSignupMetadata = z.infer<typeof landingSignupMetadataSchema>;

/**
 * Reads the landing cookie and shapes it, with the pressed button, as sign-up
 * `unsafeMetadata`.
 *
 * Always returns a value: the method is known even when the cookie is not,
 * and "signed up with email from an unknown page" is a real answer.
 *
 * @param signupMethod - Which button the visitor pressed on the sign-up screen
 */
export function getLandingSignupUnsafeMetadata(
  signupMethod: SignupMethod
): { [LANDING_SIGNUP_METADATA_KEY]: LandingSignupMetadata } {
  const landing = typeof window === "undefined" ? null : readLandingCookie();

  return {
    [LANDING_SIGNUP_METADATA_KEY]: {
      signupMethod,
      visitorId: landing?.visitorId ?? undefined,
      sourcePage: landing?.sourcePage ?? undefined,
      sourceCta: landing?.sourceCta ?? undefined,
      landingPath: landing?.landingPath ?? undefined,
    },
  };
}

/** Extracts the landing record from a user's unsafe metadata; null if absent/invalid. */
export function parseLandingSignupMetadata(
  unsafeMetadata: unknown
): LandingSignupMetadata | null {
  if (typeof unsafeMetadata !== "object" || unsafeMetadata === null) return null;
  const raw = (unsafeMetadata as Record<string, unknown>)[
    LANDING_SIGNUP_METADATA_KEY
  ];
  const parsed = landingSignupMetadataSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}
